import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Plus, 
  Play, 
  Edit, 
  Trash2, 
  Brain, 
  CheckCircle,
  AlertCircle,
  Clock,
  Trophy,
  Target,
  BookOpen
} from 'lucide-react'
import Navigation from '../components/Navigation'
import { learningService } from '../services/learningService'
import { quizGeneratorService } from '../services/quizGeneratorService'
import { LearningModule, LearningVideo } from '../types/learning'

const AdminLearnPage: React.FC = () => {
  const [modules, setModules] = useState<LearningModule[]>([])
  const [videos, setVideos] = useState<LearningVideo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'modules' | 'videos' | 'quizzes'>('modules')
  const [showAddVideo, setShowAddVideo] = useState(false)
  const [generatingQuiz, setGeneratingQuiz] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)
        
        const modulesData = await learningService.getModules()
        setModules(modulesData)

        // Get all videos from all modules
        const allVideos = []
        for (const module of modulesData) {
          const moduleVideos = await learningService.getModuleVideos(module.id)
          allVideos.push(...moduleVideos)
        }
        setVideos(allVideos)
      } catch (err) {
        console.error('Error fetching admin data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load admin data')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleGenerateQuiz = async (video: LearningVideo) => {
    try {
      setGeneratingQuiz(video.id)
      
      const videoInfo = {
        title: video.title,
        description: video.description,
        learningObjectives: video.learning_objectives,
        category: video.category,
        difficulty: video.difficulty
      }

      await quizGeneratorService.generateAndSaveQuiz(video.id, videoInfo)
      
      // Refresh videos to show updated quiz status
      const updatedVideos = await learningService.getModuleVideos(video.module_id)
      setVideos(prev => prev.map(v => v.id === video.id ? { ...v, quiz_questions: updatedVideos.find(uv => uv.id === video.id)?.quiz_questions } : v))
      
    } catch (err) {
      console.error('Error generating quiz:', err)
      alert('Failed to generate quiz: ' + (err instanceof Error ? err.message : 'Unknown error'))
    } finally {
      setGeneratingQuiz(null)
    }
  }

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-500'
      case 'intermediate': return 'text-yellow-500'
      case 'advanced': return 'text-red-500'
      default: return 'text-background-500'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background-950">
        <Navigation />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
          <div className="text-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              className="w-16 h-16 border-4 border-primary-500/20 border-t-primary-500 rounded-full mx-auto mb-6"
            />
            <p className="text-background-400">Loading admin panel...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background-950">
        <Navigation />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
          <div className="text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="luxury-button primary"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background-950">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-background-100 mb-4">
            Learning Content Admin
          </h1>
          <p className="text-background-400 text-lg">
            Manage learning modules, videos, and quizzes for the Federal Reserve learning platform.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="luxury-card p-6 text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-primary-500/20 rounded-lg mx-auto mb-3">
              <BookOpen className="w-6 h-6 text-primary-500" />
            </div>
            <div className="text-2xl font-bold text-background-100 mb-1">
              {modules.length}
            </div>
            <div className="text-sm text-background-500">
              Learning Modules
            </div>
          </div>

          <div className="luxury-card p-6 text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-green-500/20 rounded-lg mx-auto mb-3">
              <Play className="w-6 h-6 text-green-500" />
            </div>
            <div className="text-2xl font-bold text-background-100 mb-1">
              {videos.length}
            </div>
            <div className="text-sm text-background-500">
              Videos
            </div>
          </div>

          <div className="luxury-card p-6 text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-yellow-500/20 rounded-lg mx-auto mb-3">
              <Brain className="w-6 h-6 text-yellow-500" />
            </div>
            <div className="text-2xl font-bold text-background-100 mb-1">
              {videos.filter(v => v.quiz_questions && v.quiz_questions.length > 0).length}
            </div>
            <div className="text-sm text-background-500">
              Videos with Quizzes
            </div>
          </div>

          <div className="luxury-card p-6 text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-lg mx-auto mb-3">
              <Trophy className="w-6 h-6 text-purple-500" />
            </div>
            <div className="text-2xl font-bold text-background-100 mb-1">
              {videos.reduce((total, video) => total + (video.quiz_questions?.length || 0), 0)}
            </div>
            <div className="text-sm text-background-500">
              Total Quiz Questions
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="luxury-card p-6 mb-8">
          <div className="flex space-x-1 mb-6">
            <button
              onClick={() => setActiveTab('modules')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'modules'
                  ? 'bg-primary-500 text-white'
                  : 'text-background-400 hover:text-background-300'
              }`}
            >
              Modules
            </button>
            <button
              onClick={() => setActiveTab('videos')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'videos'
                  ? 'bg-primary-500 text-white'
                  : 'text-background-400 hover:text-background-300'
              }`}
            >
              Videos
            </button>
            <button
              onClick={() => setActiveTab('quizzes')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'quizzes'
                  ? 'bg-primary-500 text-white'
                  : 'text-background-400 hover:text-background-300'
              }`}
            >
              Quizzes
            </button>
          </div>

          {/* Modules Tab */}
          {activeTab === 'modules' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-background-100">
                  Learning Modules
                </h3>
                <button className="luxury-button primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Module
                </button>
              </div>
              
              {modules.map((module) => (
                <div key={module.id} className="p-4 bg-background-800 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-background-100 mb-1">
                        {module.title}
                      </h4>
                      <p className="text-sm text-background-400 mb-2">
                        {module.description}
                      </p>
                      <div className="flex items-center space-x-4 text-xs text-background-500">
                        <span className={`${getDifficultyColor(module.difficulty)}`}>
                          {module.difficulty}
                        </span>
                        <span>{module.videos?.length || 0} videos</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button className="luxury-button secondary">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="luxury-button secondary text-red-400 hover:text-red-300">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Videos Tab */}
          {activeTab === 'videos' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-background-100">
                  Learning Videos
                </h3>
                <button 
                  onClick={() => setShowAddVideo(true)}
                  className="luxury-button primary"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Video
                </button>
              </div>
              
              {videos.map((video) => (
                <div key={video.id} className="p-4 bg-background-800 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-background-100 mb-1">
                        {video.title}
                      </h4>
                      <p className="text-sm text-background-400 mb-2 line-clamp-2">
                        {video.description}
                      </p>
                      <div className="flex items-center space-x-4 text-xs text-background-500">
                        <span className={`${getDifficultyColor(video.difficulty)}`}>
                          {video.difficulty}
                        </span>
                        <span className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatDuration(video.duration)}
                        </span>
                        <span className="flex items-center">
                          <Trophy className="w-3 h-3 mr-1" />
                          {video.xp_reward} XP
                        </span>
                        <span className="flex items-center">
                          <Target className="w-3 h-3 mr-1" />
                          {video.category}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      {video.quiz_questions && video.quiz_questions.length > 0 ? (
                        <div className="flex items-center text-green-500 text-xs">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          {video.quiz_questions.length} questions
                        </div>
                      ) : (
                        <button
                          onClick={() => handleGenerateQuiz(video)}
                          disabled={generatingQuiz === video.id}
                          className="luxury-button secondary text-sm"
                        >
                          {generatingQuiz === video.id ? (
                            <>
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                className="w-4 h-4 mr-1"
                              >
                                <Brain className="w-4 h-4" />
                              </motion.div>
                              Generating...
                            </>
                          ) : (
                            <>
                              <Brain className="w-4 h-4 mr-1" />
                              Generate Quiz
                            </>
                          )}
                        </button>
                      )}
                      <button className="luxury-button secondary">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="luxury-button secondary text-red-400 hover:text-red-300">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Quizzes Tab */}
          {activeTab === 'quizzes' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-background-100">
                Quiz Management
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {videos.filter(v => v.quiz_questions && v.quiz_questions.length > 0).map((video) => (
                  <div key={video.id} className="p-4 bg-background-800 rounded-lg">
                    <h4 className="font-semibold text-background-100 mb-2">
                      {video.title}
                    </h4>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-background-500">
                        {video.quiz_questions?.length} questions
                      </div>
                      <div className="flex items-center space-x-2">
                        <button className="luxury-button secondary text-sm">
                          View Questions
                        </button>
                        <button className="luxury-button secondary text-sm">
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {videos.filter(v => v.quiz_questions && v.quiz_questions.length > 0).length === 0 && (
                <div className="text-center py-8 text-background-500">
                  <Brain className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No quizzes generated yet.</p>
                  <p className="text-sm">Generate quizzes for videos to see them here.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Add Video Modal */}
        {showAddVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddVideo(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-background-900 rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-background-100">
                  Add New Video
                </h2>
                <button
                  onClick={() => setShowAddVideo(false)}
                  className="text-background-500 hover:text-background-300"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-background-300 mb-2">
                    YouTube URL
                  </label>
                  <input
                    type="url"
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="w-full p-3 bg-background-800 border border-background-700 rounded-lg text-background-100 placeholder-background-500 focus:border-primary-500 focus:outline-none"
                  />
                </div>
                
                <div className="text-center text-background-500">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                  <p>Video management functionality will be implemented in the next phase.</p>
                </div>
                
                <div className="flex items-center justify-end space-x-4">
                  <button
                    onClick={() => setShowAddVideo(false)}
                    className="luxury-button secondary"
                  >
                    Cancel
                  </button>
                  <button className="luxury-button primary">
                    Add Video
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default AdminLearnPage
